import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTheme, alpha } from '@mui/material/styles';
import type { SectionType, StatusCheckValue, ThresholdConfig } from '../../types';

// ─── Status Check Widget ────────────────────────────────────────────────────

const STATUS_CHECK_OPTIONS: {
  value: StatusCheckValue;
  label: string;
  color: 'success' | 'error' | 'warning' | 'standard';
  icon: React.ReactNode;
}[] = [
  { value: 'PASS', label: 'Pass', color: 'success', icon: <CheckCircleIcon /> },
  { value: 'FAIL', label: 'Fail', color: 'error', icon: <CancelIcon /> },
  { value: 'WARNING', label: 'Warning', color: 'warning', icon: <WarningIcon /> },
  { value: 'NOT_APPLICABLE', label: 'N/A', color: 'standard', icon: <RemoveCircleOutlineIcon /> },
];

interface StatusCheckWidgetProps {
  value?: StatusCheckValue;
  onChange?: (value: StatusCheckValue) => void;
  disabled?: boolean;
}

export const StatusCheckWidget: React.FC<StatusCheckWidgetProps> = ({ value, onChange, disabled }) => {
  const theme = useTheme();

  if (disabled) {
    return (
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value || null}
        sx={{
          '& .MuiToggleButton-root': {
            px: 2,
            '&:first-of-type': { borderRadius: '8px 0 0 8px' },
            '&:last-of-type': { borderRadius: '0 8px 8px 0' },
          },
        }}
      >
        <ToggleButton
          value="PASS"
          disabled
          sx={{
            color: theme.palette.success.main,
            borderColor: theme.palette.success.main,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.dark,
            },
          }}
        >
          <CheckCircleOutlineIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Good
        </ToggleButton>
        <ToggleButton
          value="WARNING"
          disabled
          sx={{
            color: '#f57c00',
            borderColor: '#f57c00',
            '&.Mui-selected': {
              bgcolor: alpha('#f57c00', 0.12),
              color: '#e65100',
            },
          }}
        >
          <CancelOutlinedIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Monitor
        </ToggleButton>
        <ToggleButton
          value="FAIL"
          disabled
          sx={{
            color: theme.palette.error.main,
            borderColor: theme.palette.error.main,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.dark,
            },
          }}
        >
          <CancelOutlinedIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Critical
        </ToggleButton>
      </ToggleButtonGroup>
    );
  }

  return (
    <ToggleButtonGroup
      value={value || null}
      exclusive
      onChange={(_event, newValue) => {
        if (newValue !== null && onChange) {
          onChange(newValue as StatusCheckValue);
        }
      }}
      aria-label="Status check selection"
      sx={{ flexWrap: 'wrap' }}
    >
      {STATUS_CHECK_OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          color={option.color}
          aria-label={option.label}
          sx={{ px: 2, py: 1 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            {option.icon}
            <Typography variant="body2">{option.label}</Typography>
          </Stack>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

// ─── Threshold Indicator ────────────────────────────────────────────────────

interface ThresholdIndicatorProps {
  thresholds: ThresholdConfig;
  currentValue?: number;
}

export const ThresholdIndicator: React.FC<ThresholdIndicatorProps> = ({
  thresholds,
  currentValue,
}) => {
  const { warningMin, warningMax, criticalMin, criticalMax, unit } = thresholds;

  const getValueStatus = (): 'normal' | 'warning' | 'critical' | undefined => {
    if (currentValue === undefined || currentValue === null) return undefined;
    if (criticalMax !== undefined && currentValue > criticalMax) return 'critical';
    if (criticalMin !== undefined && currentValue < criticalMin) return 'critical';
    if (warningMax !== undefined && currentValue > warningMax) return 'warning';
    if (warningMin !== undefined && currentValue < warningMin) return 'warning';
    return 'normal';
  };

  const valueStatus = getValueStatus();

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
      {criticalMin !== undefined && (
        <Chip size="small" label={`Critical Min: ${criticalMin}${unit ? ` ${unit}` : ''}`} color="error" variant="outlined" />
      )}
      {warningMin !== undefined && (
        <Chip size="small" label={`Warning Min: ${warningMin}${unit ? ` ${unit}` : ''}`} color="warning" variant="outlined" />
      )}
      {warningMax !== undefined && (
        <Chip size="small" label={`Warning Max: ${warningMax}${unit ? ` ${unit}` : ''}`} color="warning" variant="outlined" />
      )}
      {criticalMax !== undefined && (
        <Chip size="small" label={`Critical Max: ${criticalMax}${unit ? ` ${unit}` : ''}`} color="error" variant="outlined" />
      )}
      {valueStatus === 'warning' && (
        <Chip size="small" label="⚠ Value in warning range" color="warning" />
      )}
      {valueStatus === 'critical' && (
        <Chip size="small" label="⛔ Value in critical range" color="error" />
      )}
    </Stack>
  );
};

// ─── Number Entry Widget ────────────────────────────────────────────────────

interface NumberEntryWidgetProps {
  value?: number;
  onChange?: (value: number | undefined) => void;
  thresholds?: ThresholdConfig;
  disabled?: boolean;
}

export const NumberEntryWidget: React.FC<NumberEntryWidgetProps> = ({
  value,
  onChange,
  thresholds,
  disabled,
}) => {
  return (
    <Box>
      <TextField
        type="number"
        value={value !== undefined && value !== null ? value : ''}
        onChange={(e) => {
          if (!onChange) return;
          const raw = e.target.value;
          if (raw === '') {
            onChange(undefined);
          } else {
            const parsed = parseFloat(raw);
            onChange(isNaN(parsed) ? undefined : parsed);
          }
        }}
        placeholder="Enter numeric value"
        fullWidth
        size="small"
        disabled={disabled}
        inputProps={{ inputMode: 'decimal', step: 'any' }}
        aria-label="Numeric entry"
      />
      {thresholds && <ThresholdIndicator thresholds={thresholds} currentValue={value} />}
    </Box>
  );
};

// ─── Text Result Widget ─────────────────────────────────────────────────────

interface TextResultWidgetProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export const TextResultWidget: React.FC<TextResultWidgetProps> = ({ value, onChange, disabled }) => {
  return (
    <TextField
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      multiline
      rows={3}
      size="small"
      placeholder="Enter observations..."
      fullWidth
      disabled={disabled}
      aria-label="Text observation entry"
    />
  );
};

// ─── Guidance Image (with lightbox) ─────────────────────────────────────────

interface GuidanceImageProps {
  imageUrl: string;
  altText: string;
}

export const GuidanceImage: React.FC<GuidanceImageProps> = ({ imageUrl, altText }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  return (
    <>
      <Box sx={{ position: 'relative', cursor: 'pointer', mb: 1 }}>
        <CardMedia
          component="img"
          image={imageUrl}
          alt={altText}
          sx={{
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
          onClick={() => setLightboxOpen(true)}
        />
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'background.default' },
          }}
          onClick={() => setLightboxOpen(true)}
          aria-label="Zoom guidance image"
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>

      <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 1, position: 'relative' }}>
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
            aria-label="Close lightbox"
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={imageUrl}
            alt={altText}
            sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Multi Value Widget ─────────────────────────────────────────────────────

interface Reading {
  label?: string;
  numericValue?: number;
  textValue?: string;
}

interface MultiValueWidgetProps {
  readings?: Reading[];
  computedAverage?: number;
  onChange?: (readings: Reading[]) => void;
  disabled?: boolean;
}

export const MultiValueWidget: React.FC<MultiValueWidgetProps> = ({
  readings,
  computedAverage,
  onChange,
  disabled,
}) => {
  const rows = readings ?? [];

  const update = (next: Reading[]) => onChange?.(next);

  const addRow = () => update([...rows, { numericValue: undefined }]);
  const removeRow = (idx: number) => update(rows.filter((_, i) => i !== idx));
  const setValue = (idx: number, numericValue: number | undefined) =>
    update(rows.map((r, i) => (i === idx ? { ...r, numericValue } : r)));

  return (
    <Box>
      <Stack spacing={1}>
        {rows.map((r, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
              #{idx + 1}
            </Typography>
            <TextField
              type="number"
              size="small"
              value={r.numericValue ?? ''}
              onChange={(e) => setValue(idx, e.target.value === '' ? undefined : Number(e.target.value))}
              disabled={disabled}
              sx={{ flex: 1 }}
              placeholder="Reading"
            />
            <IconButton size="small" color="error" onClick={() => removeRow(idx)} disabled={disabled}>
              <RemoveCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button
        size="small"
        startIcon={<AddCircleOutlineIcon />}
        onClick={addRow}
        disabled={disabled}
        sx={{ mt: 1 }}
      >
        Add Reading
      </Button>
      {computedAverage != null && (
        <Chip
          size="small"
          label={`Average: ${computedAverage.toFixed(2)}`}
          color="info"
          variant="outlined"
          sx={{ ml: 1, mt: 1 }}
        />
      )}
    </Box>
  );
};

// ─── Section Card ───────────────────────────────────────────────────────────

export interface SectionCardAnswer {
  sectionId: string;
  sectionType: SectionType;
  statusValue?: StatusCheckValue;
  numericValue?: number;
  textValue?: string;
  readings?: Array<{ label?: string; numericValue?: number; textValue?: string }>;
  computedAverage?: number;
}

interface SectionCardProps {
  sectionId: string;
  question: string;
  description?: string;
  sectionType: SectionType;
  index: number;
  guidanceImageUrl?: string;
  areaName?: string;
  machinePartId?: string;
  measurementKey?: string;
  thresholds?: ThresholdConfig;
  answer?: SectionCardAnswer;
  onAnswerChange?: (answer: SectionCardAnswer) => void;
  disabled?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  sectionId,
  question,
  description,
  sectionType,
  index,
  guidanceImageUrl,
  areaName,
  machinePartId,
  measurementKey,
  thresholds,
  answer,
  onAnswerChange,
  disabled,
}) => {
  const handleStatusChange = (statusValue: StatusCheckValue) => {
    onAnswerChange?.({ sectionId, sectionType, statusValue });
  };

  const handleNumberChange = (numericValue: number | undefined) => {
    onAnswerChange?.({ sectionId, sectionType, numericValue });
  };

  const handleTextChange = (textValue: string) => {
    onAnswerChange?.({ sectionId, sectionType, textValue });
  };

  const handleReadingsChange = (readings: Array<{ label?: string; numericValue?: number; textValue?: string }>) => {
    const nums = readings.map((r) => r.numericValue).filter((n): n is number => n != null);
    const computedAverage = nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : undefined;
    onAnswerChange?.({ sectionId, sectionType, readings, computedAverage });
  };

  return (
    <Card
      sx={{
        mb: 2,
        borderLeft: 4,
        borderLeftColor:
          sectionType === 'STATUS_CHECK'
            ? 'primary.main'
            : sectionType === 'NUMBER_ENTRY'
            ? 'secondary.main'
            : 'info.main',
      }}
      variant="outlined"
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip size="small" label={`#${index + 1}`} color="primary" variant="outlined" />
          <Chip
            size="small"
            label={sectionType.replace('_', ' ')}
            variant="filled"
            color={
              sectionType === 'STATUS_CHECK'
                ? 'primary'
                : sectionType === 'NUMBER_ENTRY'
                ? 'secondary'
                : 'info'
            }
          />
          {areaName && (
            <Chip size="small" label={`Area: ${areaName}`} variant="outlined" color="default" />
          )}
          {machinePartId && (
            <Chip size="small" label={`Part: ${machinePartId}`} variant="outlined" color="default" />
          )}
          {measurementKey && (
            <Chip size="small" label={`Measure: ${measurementKey}`} variant="outlined" color="default" />
          )}
        </Stack>

        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {question || 'Untitled Question'}
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {description}
          </Typography>
        )}

        {guidanceImageUrl && (
          <GuidanceImage imageUrl={guidanceImageUrl} altText={`Guidance for: ${question}`} />
        )}

        <Box sx={{ mt: 2 }}>
          {sectionType === 'STATUS_CHECK' && (
            <StatusCheckWidget
              value={answer?.statusValue}
              onChange={handleStatusChange}
              disabled={disabled}
            />
          )}
          {sectionType === 'NUMBER_ENTRY' && (
            <NumberEntryWidget
              value={answer?.numericValue}
              onChange={handleNumberChange}
              thresholds={thresholds}
              disabled={disabled}
            />
          )}
          {sectionType === 'TEXT_RESULT' && (
            <TextResultWidget
              value={answer?.textValue}
              onChange={handleTextChange}
              disabled={disabled}
            />
          )}
          {sectionType === 'MULTI_VALUE' && (
            <MultiValueWidget
              readings={answer?.readings}
              computedAverage={answer?.computedAverage}
              onChange={handleReadingsChange}
              disabled={disabled}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
